import React, { useState } from "react";
import { COLORS } from "../config/themes";
import { Modal, Button, InputField, SelectField } from "../components";
import { submitAbsenceRequest, syncTeacherAttendance } from "../utils/db";
import { supabase } from "../supabaseClient";

const REASONS = [
  { value: "sick", label: "Sick / Illness" },
  { value: "personal", label: "Personal Day" },
  { value: "family", label: "Family Emergency" },
  { value: "medical", label: "Medical Appointment" },
  { value: "bereavement", label: "Bereavement" },
  { value: "other", label: "Other" },
];

const todayStr = () => new Date().toISOString().split("T")[0];

const addDays = (dateStr, n) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

const TeacherAbsenceForm = ({ open, onClose, currentUser, schoolId, teacherAttendance, setTeacherAttendance, onSubmitted }) => {
  const [type, setType] = useState("out");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [multiDay, setMultiDay] = useState(false);
  const [estimatedArrival, setEstimatedArrival] = useState("09:00");
  const [reason, setReason] = useState("sick");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setType("out"); setStartDate(todayStr()); setEndDate(todayStr());
    setMultiDay(false); setEstimatedArrival("09:00"); setReason("sick");
    setNote(""); setError("");
  };

  const handleClose = () => { reset(); onClose(); };

  // Generate list of dates between start and end (weekdays only)
  const getDateRange = () => {
    if (type === "late") return [startDate];
    if (!multiDay) return [startDate];
    const dates = [];
    let current = startDate;
    while (current <= endDate) {
      const day = new Date(current + "T12:00:00").getDay();
      if (day !== 0 && day !== 6) dates.push(current); // skip weekends
      current = addDays(current, 1);
    }
    return dates;
  };

  const selectedDates = getDateRange();

  const handleSubmit = async () => {
    if (!startDate) { setError("Please select a date."); return; }
    if (multiDay && endDate < startDate) { setError("End date must be after start date."); return; }
    if (selectedDates.length === 0) { setError("No weekdays selected in that range."); return; }

    setLoading(true); setError("");
    try {
      // 1. Save the absence request record
      await submitAbsenceRequest({
        teacherId: currentUser.id,
        type,
        dates: selectedDates,
        estimatedArrival: type === "late" ? estimatedArrival : null,
        reason: type === "out" ? reason : null,
        note,
      }, schoolId);

      // 2. Write attendance directly to Supabase for selected dates only
      const rows = selectedDates.map(date => ({
        school_id: schoolId,
        teacher_id: currentUser.id,
        date,
        status: type === "late" ? "late" : "called-out",
      }));
      const { error: attError } = await supabase
        .from("teacher_attendance")
        .upsert(rows, { onConflict: "school_id,teacher_id,date" });
      if (attError) console.error("Attendance sync error:", attError);

      // Update local state
      const newAttendance = { ...teacherAttendance };
      selectedDates.forEach(date => {
        if (!newAttendance[date]) newAttendance[date] = {};
        newAttendance[date][currentUser.id] = type === "late" ? "late" : "called-out";
      });
      setTeacherAttendance(newAttendance);

      // 3. Send email notification to admin via send-email edge function
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const { data: school } = await supabase.from("schools").select("name").eq("id", schoolId).single();
          const { data: admins } = await supabase.from("profiles").select("email, name").eq("school_id", schoolId).eq("role", "admin");
          if (admins?.length > 0) {
            const typeLabel = type === "late" ? "Late Arrival" : "Absence";
            const dateLabel = selectedDates.length === 1
              ? formatDate(selectedDates[0])
              : `${formatDate(selectedDates[0])} – ${formatDate(selectedDates[selectedDates.length - 1])} (${selectedDates.length} days)`;

            for (const admin of admins) {
              await supabase.functions.invoke("send-email", {
                body: {
                  type: "teacher-absence",
                  payload: {
                    adminName: admin.name || "Admin",
                    adminEmail: admin.email,
                    teacherName: currentUser.name,
                    schoolName: school?.name || "your school",
                    absenceType: typeLabel,
                    dates: dateLabel,
                    reason: type === "out" ? REASONS.find(r => r.value === reason)?.label : `Estimated arrival: ${estimatedArrival}`,
                    note: note || "No additional notes.",
                  }
                }
              });
            }
          }
        }
      } catch (emailErr) {
        console.warn("Absence notification email failed (non-fatal):", emailErr);
      }

      onSubmitted?.();
      handleClose();
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Modal open={open} onClose={handleClose} title="Report Absence or Late Arrival">
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* Type selector */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>I will be</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { value: "out", label: "Out", icon: <svg width="22" height="22" viewBox="0 0 256 256" fill="currentColor" style={{color: COLORS.danger}}><path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24zm37.66 130.34a8 8 0 0 1-11.32 11.32L128 139.31l-26.34 26.35a8 8 0 0 1-11.32-11.32L116.69 128 90.34 101.66a8 8 0 0 1 11.32-11.32L128 116.69l26.34-26.35a8 8 0 0 1 11.32 11.32L139.31 128z"/></svg>, desc: "Not coming in" },
              { value: "late", label: "Coming in Late", icon: <svg width="22" height="22" viewBox="0 0 256 256" fill="currentColor" style={{color: COLORS.warning}}><path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24zm0 192a88 88 0 1 1 88-88 88.1 88.1 0 0 1-88 88zm64-88a8 8 0 0 1-8 8h-56a8 8 0 0 1-8-8V72a8 8 0 0 1 16 0v56h48a8 8 0 0 1 8 8z"/></svg>, desc: "Arriving after start time" },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setType(opt.value)}
                style={{ padding: "14px 16px", borderRadius: 12, border: `2px solid ${type === opt.value ? COLORS.primary : COLORS.border}`, background: type === opt.value ? COLORS.primaryPale : COLORS.card, cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "all 0.2s" }}
              >
                <div style={{ marginBottom: 6 }}>{opt.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: type === opt.value ? COLORS.primary : COLORS.navy }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Date selection */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
            {type === "late" ? "Date" : "Date(s)"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: multiDay ? "1fr 1fr" : "1fr", gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>{multiDay ? "From" : "Date"}</label>
                <input
                  type="date"
                  value={startDate}
                  min={todayStr()}
                  onChange={e => { setStartDate(e.target.value); if (e.target.value > endDate) setEndDate(e.target.value); }}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${COLORS.border}`, fontSize: 14, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, boxSizing: "border-box" }}
                />
              </div>
              {multiDay && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>To</label>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={e => setEndDate(e.target.value)}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${COLORS.border}`, fontSize: 14, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, boxSizing: "border-box" }}
                  />
                </div>
              )}
            </div>

            {type === "out" && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: COLORS.textMuted, fontWeight: 500 }}>
                <input type="checkbox" checked={multiDay} onChange={e => setMultiDay(e.target.checked)} style={{ accentColor: COLORS.primary }} />
                Report multiple days
              </label>
            )}

            {/* Date preview */}
            {selectedDates.length > 0 && (
              <div style={{ padding: "10px 14px", background: COLORS.primaryPale, borderRadius: 10, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: COLORS.primary }}>
                  {selectedDates.length === 1 ? formatDate(selectedDates[0]) : `${selectedDates.length} days: ${formatDate(selectedDates[0])} → ${formatDate(selectedDates[selectedDates.length - 1])}`}
                </span>
                {multiDay && selectedDates.length < (new Date(endDate) - new Date(startDate)) / 86400000 + 1 && (
                  <span style={{ color: COLORS.textMuted, marginLeft: 8 }}>(weekdays only)</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Late arrival time */}
        {type === "late" && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>Estimated Arrival Time</label>
            <input
              type="time"
              value={estimatedArrival}
              onChange={e => setEstimatedArrival(e.target.value)}
              style={{ padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${COLORS.border}`, fontSize: 14, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, width: "100%", boxSizing: "border-box" }}
            />
          </div>
        )}

        {/* Reason (for out only) */}
        {type === "out" && (
          <SelectField
            label="Reason"
            value={reason}
            onChange={e => setReason(e.target.value)}
            options={REASONS}
          />
        )}

        {/* Note */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>Additional Note <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Any additional information for your admin..."
            rows={3}
            style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${COLORS.border}`, fontSize: 14, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, resize: "vertical", boxSizing: "border-box" }}
          />
        </div>

        {error && (
          <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.dangerPale, color: COLORS.danger, fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting..." : `Submit ${type === "late" ? "Late Arrival" : "Absence"}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TeacherAbsenceForm;
